/**
 * Alarms API client — consumes the Phase 3 nested alarm routes mounted at
 * /api/v1/sites/:siteId/alarms. Axios baseURL already includes '/api', so
 * call paths use '/v1/...' here (matching existing service conventions).
 *
 * Pane-of-glass constraint: this client is read-only by design. There are no
 * ack / clear / resolve methods — the UI never mutates alarm state. Edge
 * transitions are the single source of truth.
 */

import api from './api';
import type {
  ActiveAlarm,
  AlarmsListResponse,
  AlarmDetailResponse,
  AlarmSummary,
  AlarmHistoryEvent,
  SiteDevice,
} from '../types/alarm.types';

export interface ListFilters {
  status?: string;
  severity?: string;
  device_type?: string;
  block_id?: string;
}

/**
 * Filters for the customer-facing global list (`GET /api/v1/alarms`).
 * No `block_id` — global view groups by block client-side after fetch
 * (block_id is only unique within a site, so it's not a useful global
 * filter).
 */
export interface CustomerListFilters {
  status?: string;
  severity?: string;
  device_type?: string;
  /**
   * ISO 8601 lower bound for raised_at. Only sent when status='history'
   * (History tab time-window filter). Must never be sent for active buckets —
   * it would hide alarms operators must not miss.
   */
  raised_after?: string;
}

export interface HistoryFilters {
  device_id?: string;
  condition_key?: string;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
}

/**
 * Trailing window applied to the site History tab (#434). Product wants the
 * last 30 days; alarm_history is retained for ~13 months (396 days, pruned
 * application-side by AlarmRetentionService — issue #647 found the declared
 * Timescale policy had never applied), so this is purely a filter.
 */
export const HISTORY_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Per-request cap for the History tab. alarm_history is paginated by cursor,
 * but the tab renders a flat list — pull a single generous page that covers a
 * busy site's 30-day cleared set (Turner ~535) without a pagination UI.
 */
const HISTORY_TAB_LIMIT = 1000;

/**
 * Project a terminal `cleared` history event back into an ActiveAlarm-shaped
 * row so the History tab can reuse the active-alarm tree + detail components
 * (which consume ActiveAlarm[]). alarm_history carries no live cleared_at /
 * acked_at columns — those live in the immutable `lifecycle` JSONB, so we lift
 * them out (matching export.routes.ts). `cleared_at` being set is what makes
 * alarmBucket() classify the row as `history`.
 */
function clearedEventToActiveAlarm(e: AlarmHistoryEvent): ActiveAlarm {
  const lc = (e.lifecycle ?? {}) as {
    acknowledged_at?: string;
    acknowledged_by?: string;
    cleared_at?: string;
  };
  return {
    site_id: e.site_id,
    device_id: e.device_id,
    condition_key: e.condition_key,
    customer_id: e.customer_id ?? '',
    device_type: e.device_type,
    block_id: e.block_id,
    condition_label: e.condition_label,
    category: e.category,
    severity: e.severity,
    // alarm_history rows don't carry the original raised_at; the cleared
    // event's ts is the best available occurrence time for a resolved alarm.
    raised_at: e.ts,
    last_event_id: e.event_id,
    last_ts: e.ts,
    value: e.value,
    metadata: e.metadata,
    acked_at: lc.acknowledged_at ?? null,
    acked_by: lc.acknowledged_by ?? null,
    underlying_cleared_at: null,
    cleared_at: lc.cleared_at ?? e.ts,
    stale_since: null,
    latching: false,
  };
}

export const alarmsApi = {
  /**
   * List active alarms for a site with optional filters.
   * Backing route: GET /api/v1/sites/:siteId/alarms
   */
  listForSite: async (siteId: string, filters?: ListFilters): Promise<AlarmsListResponse> => {
    const response = await api.get<AlarmsListResponse>(
      `/v1/sites/${encodeURIComponent(siteId)}/alarms`,
      { params: filters },
    );
    return response.data;
  },

  /**
   * List active alarms across all sites accessible to the caller's customer.
   * Powers the customer-facing global Alarms hub at `/alarms`.
   * Backing route: GET /api/v1/alarms (Phase 5, EPIC #225 sub 6/10).
   *
   * Response shape differs from listForSite — no `summary` block (the global
   * page derives counts from the row list itself, since cross-site summary
   * has no single canonical site/block scope).
   *
   * `truncated` is set only on the History bucket (status='history'), where the
   * server caps the list at 1000 rows newest-first (#632). It must be surfaced,
   * not dropped: a capped list is visually identical to a complete one, so
   * discarding the flag turns "the newest 1000 of 21,000" into an answer that
   * looks authoritative and is wrong (#639).
   */
  listAcrossSites: async (
    filters?: CustomerListFilters,
  ): Promise<{
    alarms: AlarmsListResponse['alarms'];
    total: number;
    truncated?: boolean;
  }> => {
    const response = await api.get<{
      alarms: AlarmsListResponse['alarms'];
      total: number;
      truncated?: boolean;
    }>('/v1/alarms', { params: filters });
    return response.data;
  },

  /**
   * Site-wide summary for tree badges.
   * Backing route: GET /api/v1/sites/:siteId/alarms/summary
   */
  getSummary: async (siteId: string): Promise<AlarmSummary> => {
    const response = await api.get<AlarmSummary>(
      `/v1/sites/${encodeURIComponent(siteId)}/alarms/summary`,
    );
    return response.data;
  },

  /**
   * Identity-detail for a single (site, device, condition) tuple — the
   * active row + last N transitions. Powers the detail panel timeline.
   * Backing route: GET /api/v1/sites/:siteId/alarms/:deviceId/:conditionKey
   */
  getDetail: async (
    siteId: string,
    deviceId: string,
    conditionKey: string,
  ): Promise<AlarmDetailResponse> => {
    const response = await api.get<AlarmDetailResponse>(
      `/v1/sites/${encodeURIComponent(siteId)}/alarms/${encodeURIComponent(deviceId)}/${encodeURIComponent(conditionKey)}`,
    );
    return response.data;
  },

  /**
   * Alarm history with cursor pagination.
   * Backing route: GET /api/v1/sites/:siteId/alarms/history
   */
  queryHistory: async (
    siteId: string,
    filters?: HistoryFilters,
  ): Promise<{ events: AlarmHistoryEvent[]; next_cursor: string | null }> => {
    const response = await api.get<{ events: AlarmHistoryEvent[]; next_cursor: string | null }>(
      `/v1/sites/${encodeURIComponent(siteId)}/alarms/history`,
      { params: filters },
    );
    return response.data;
  },

  /**
   * History bucket for the site Alarms page (#434). The History tab previously
   * called listForSite({ status: 'history' }), which reads alarms.active_alarms
   * — but cleared rows are purged from that table ~60s after clearing, so the
   * tab showed almost nothing. The durable record lives in alarms.alarm_history
   * (~13-month retention, enforced application-side — #647), so read that via
   * queryHistory() instead and project
   * the terminal `cleared` events back into ActiveAlarm rows.
   *
   * Returns the same AlarmsListResponse shape as listForSite so SiteAlarms (and
   * its tree/detail components) consume it unchanged. severity / device_type /
   * block_id are filtered client-side because /alarms/history filters by
   * identity + time window only.
   */
  listHistoryForSite: async (
    siteId: string,
    filters?: ListFilters,
  ): Promise<AlarmsListResponse> => {
    const from = new Date(Date.now() - HISTORY_WINDOW_MS).toISOString();
    const { events } = await alarmsApi.queryHistory(siteId, { from, limit: HISTORY_TAB_LIMIT });

    let rows = events
      .filter((e) => e.transition === 'cleared')
      .map(clearedEventToActiveAlarm);
    if (filters?.severity) rows = rows.filter((r) => r.severity === filters.severity);
    if (filters?.device_type) rows = rows.filter((r) => r.device_type === filters.device_type);
    if (filters?.block_id) rows = rows.filter((r) => r.block_id === filters.block_id);

    return {
      alarms: rows,
      total: rows.length,
      // History rows are all cleared, so the operator-actionable summary
      // (uncleared awaiting_ack / stale) is zero by definition; SiteAlarms
      // recomputes its own severity chips from the row list anyway.
      summary: {
        critical: rows.filter((r) => r.severity === 'critical').length,
        warning: rows.filter((r) => r.severity === 'warning').length,
        awaiting_ack: 0,
        stale: 0,
      },
    };
  },

  /**
   * Device roster for the tree. Sourced from config.site_devices (soft-delete
   * filter via removed_at IS NULL), scoped by customer via the same pattern
   * as other /sites/:id/* routes.
   * Backing route: GET /api/v1/sites/:siteId/devices
   */
  listDevices: async (siteId: string): Promise<SiteDevice[]> => {
    const response = await api.get<SiteDevice[]>(
      `/v1/sites/${encodeURIComponent(siteId)}/devices`,
    );
    return response.data;
  },
};
